<template>
  <div>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    :close-on-click-modal="false"
    append-to-body
    top="4vh"
    width="1180px"
    custom-class="print-label-qty-dialog"
    @close="handleClose"
  >
    <div class="plq-body" :class="{ 'is-busy': isPageLoading }">
      <div v-if="isPageLoading" class="plq-skeleton" aria-hidden="true">
        <div class="plq-skeleton__steps">
          <div class="plq-skeleton__step"></div>
          <div class="plq-skeleton__step"></div>
          <div class="plq-skeleton__step"></div>
          <div class="plq-skeleton__step"></div>
        </div>
        <div class="plq-skeleton__bar"></div>
        <div class="plq-skeleton__bar"></div>
        <div class="plq-skeleton__main">
          <div class="plq-skeleton__card">
            <div class="plq-skeleton__line w-40"></div>
            <div class="plq-skeleton__modes">
              <div class="plq-skeleton__mode"></div>
              <div class="plq-skeleton__mode"></div>
              <div class="plq-skeleton__mode"></div>
            </div>
            <div class="plq-skeleton__table">
              <div class="plq-skeleton__line"></div>
              <div class="plq-skeleton__line"></div>
              <div class="plq-skeleton__line w-70"></div>
            </div>
          </div>
          <div class="plq-skeleton__card plq-skeleton__preview">
            <div class="plq-skeleton__line w-50"></div>
            <div class="plq-skeleton__paper"></div>
            <div class="plq-skeleton__paper"></div>
          </div>
        </div>
      </div>
      <div class="plq-body__inner">
    <div class="plq-steps">
      <div v-if="needSpecSelect" class="plq-step" :class="stepSpecClass">
        <span class="plq-dot">1</span>
        {{ $t("选择规格") }}
      </div>
      <div class="plq-step" :class="step1Class">
        <span class="plq-dot">{{ needSpecSelect ? 2 : 1 }}</span>
        {{ $t("选择打印模板") }}
      </div>
      <div class="plq-step" :class="step2Class">
        <span class="plq-dot">{{ needSpecSelect ? 3 : 2 }}</span>
        {{ reprintMode ? $t("填写份数") : $t("填写数量并生成标签") }}
      </div>
      <div class="plq-step">
        <span class="plq-dot">{{ needSpecSelect ? 4 : 3 }}</span>
        {{ $t("打印") }}
      </div>
    </div>

    <div v-if="needSpecSelect" class="plq-bar" v-loading="specLoading">
      <span class="plq-bar__title">① {{ $t("选择规格") }}</span>
      <div class="plq-bar__list">
        <div
          v-for="row in multiSpecRows"
          :key="String(row.materialId)"
          class="plq-bar__item"
        >
          <span class="plq-bar__name" :title="row.baseMaterialNumber + ' ' + row.baseMaterialName">
            {{ row.baseMaterialNumber }} {{ row.baseMaterialName }}
          </span>
          <el-select
            v-model="row._specGroupId"
            size="mini"
            filterable
            :placeholder="$t('请选择规格')"
            class="plq-bar__select"
            @change="onSpecChange(row)"
          >
            <el-option
              v-for="spec in specOptionsOf(row)"
              :key="String(spec.specGroupId)"
              :label="specOptionLabel(spec)"
              :value="spec.specGroupId"
            />
          </el-select>
        </div>
      </div>
    </div>

    <div class="plq-bar">
      <span class="plq-bar__title">{{ needSpecSelect ? "②" : "①" }} {{ $t("选择打印模板") }}</span>
      <div class="plq-bar__list">
        <span class="plq-tpl__label">{{ $t("内置打印") }}</span>
        <el-select
          v-model="selectedTemplateId"
          size="mini"
          filterable
          :loading="templateLoading"
          :placeholder="$t('请选择')"
          style="width: 360px"
          @change="onTemplateChange"
        >
          <el-option
            v-for="item in templateList"
            :key="String(item.sysPrintId)"
            :value="item.sysPrintId"
            :label="item.sysPrintName"
          >
            <div class="plq-tpl__opt">
              <span>{{ item.sysPrintName }}</span>
              <span class="plq-tpl__type">{{ item.sysPrintType }}</span>
            </div>
          </el-option>
        </el-select>
        <span v-if="!templateLoading && !templateList.length" class="plq-tpl__empty">
          {{ $t("暂无可用") }}{{ $t("产品标签") }}{{ $t("类型模板") }}
        </span>
      </div>
    </div>

    <div class="plq-layout">
      <div class="plq-card">
        <div class="plq-card__hd">
          {{ needSpecSelect ? "③" : "②" }}
          {{ reprintMode ? $t("填写份数") : $t("填写数量") }}
          <template v-if="!reprintMode"> · {{ $t("出签方式") }}</template>
        </div>
        <div class="plq-card__bd">
          <div v-if="!reprintMode" class="plq-modes">
            <div
              class="plq-mode"
              :class="{ active: mode === 'onLabel' }"
              @click="setMode('onLabel')"
            >
              <b>{{ $t("印在标签上") }}</b>
              <span>{{ $t("每条1张，标签上印填写的数量") }}</span>
            </div>
            <div
              class="plq-mode"
              :class="{ active: mode === 'perPiece' }"
              @click="setMode('perPiece')"
            >
              <b>{{ $t("一件一签") }}</b>
              <span>{{ $t("按数量拆成多张，每张印1") }}</span>
            </div>
            <div
              class="plq-mode"
              :class="{ active: mode === 'perPack' }"
              @click="setMode('perPack')"
            >
              <b>{{ $t("按每包拆签") }}</b>
              <span>{{ $t("按每包数量拆成多张，尾包单独一张") }}</span>
            </div>
          </div>
          <div v-if="mode === 'perPack'" class="plq-pack">
            {{ $t("每包标准数量") }}
            <el-input-number
              v-model="packQty"
              :min="1"
              :controls="false"
              size="mini"
              style="width: 88px"
              @change="rebuildLabels"
            />
          </div>
          <el-table :data="qtyRows" border size="mini" max-height="180" class="plq-table">
            <el-table-column :label="noLabel" min-width="120" prop="baseMaterialNumber" />
            <el-table-column :label="nameLabel" min-width="140" prop="baseMaterialName" />
            <el-table-column :label="$t('规格')" min-width="140" :show-overflow-tooltip="true">
              <template slot-scope="scope">{{ formatSpec(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              v-if="reprintMode"
              :label="$t('批次号')"
              min-width="140"
              prop="batchNumber"
              :show-overflow-tooltip="true"
            />
            <el-table-column v-if="!reprintMode" :label="$t('单位')" width="70">
              <template slot-scope="scope">{{ formatUnit(scope.row) }}</template>
            </el-table-column>
            <el-table-column :label="$t('打印数量')" width="110">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row._printQty"
                  :min="1"
                  :controls="false"
                  size="mini"
                  style="width: 88px"
                  @change="rebuildLabels"
                />
              </template>
            </el-table-column>
          </el-table>
          <div class="plq-summary">
            {{ $t("共将生成") }}
            <strong>{{ labelList.length }}</strong>
            {{ $t("张标签") }}
            <span>（{{ modeText }}）</span>
          </div>
          <div class="plq-note">
            <template v-if="reprintMode">
              {{ $t("默认每批次1张，打印数量为补打份数") }}。
              {{ $t("批次码按物料、规格、批次生成") }}
            </template>
            <template v-else>
              {{ $t("数量会写入打印数据，供打印模板绑定数量字段") }}。
              {{ $t("标签码将在打印时生成") }}
            </template>
          </div>
        </div>
      </div>

      <div class="plq-card plq-preview">
        <div class="plq-card__hd">
          {{ selectedTemplateName }} · {{ $t("标签实时预览") }}
          <el-button
            type="text"
            size="mini"
            class="plq-preview__zoom"
            :disabled="!canPreview || isPageLoading"
            @click="openLargePreview"
          >{{ $t("放大预览") }}</el-button>
        </div>
        <div class="plq-card__bd plq-preview__bd">
          <div v-if="previewError" class="plq-preview__error">{{ previewError }}</div>
          <div ref="labelTplPreview" class="plq-tpl-preview"></div>
          <div v-if="previewLoading" class="plq-preview__loading">
            {{ $t("预览加载中") }}...
          </div>
          <div v-if="!previewHasContent && !previewLoading" class="plq-empty">
            {{ previewError || $t("暂无预览") }}
          </div>
          <div v-if="previewTip" class="plq-preview__tip">{{ previewTip }}</div>
        </div>
      </div>
    </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">{{ $t("取消") }}</el-button>
      <el-button size="small" :disabled="isPageLoading" @click="rebuildLabels">{{ $t("重新生成") }}</el-button>
      <el-button
        size="small"
        type="primary"
        class="btn-teal"
        :disabled="!canPreview || isPageLoading"
        @click="handleConfirm"
      >
        {{ confirmLoading ? $t("加载中") : $t("打印") }}
      </el-button>
    </div>
    </el-dialog>
    <print-preview
      ref="labelPrintPreview"
      :modules="modules"
    />
  </div>
</template>

<script>
import { sysPrint_list, sysNumber_queryNumberLength } from "@/api/myController/system";
import { isOfficeFileTemplateType } from "@/utils/officePrintTemplateLabels";
import { lastTemplateStorageKey, resolvePreferredLabelTemplate, LABEL_QRCODE_MODULES } from "@/utils/printLabelRows";
import printPreview from "@/views/supplier/preview";
import { hiprint } from "vue-plugin-hiprint";
import { createNormalizedHiprintTemplate } from "@/utils/printTemplatePaper";
import $ from "jquery";
import projectConfig from "@/core/config";

if (typeof window !== "undefined") {
  window.$ = window.$ || $;
  window.jQuery = window.jQuery || $;
}

const PREVIEW_MAX = 6;

function parseSpecList(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

function specListShow(list) {
  let text = "";
  try {
    for (let i = 0; i < list.length; i++) {
      const kong = i == list.length - 1 ? "" : "/";
      if (
        list.length == 1 &&
        (list[i].specKey == "规格" || list[i].specKey == "默认规格组")
      ) {
        text += (list[i].specValue || "") + kong;
      } else {
        text +=
          (list[i].specKey || "") + ":" + (list[i].specValue || "") + kong;
      }
    }
  } catch (e) {
    /* ignore */
  }
  return text;
}

export default {
  name: "printLabelQty",
  components: { printPreview },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    changeList: {
      type: Array,
      default() {
        return [];
      },
    },
    labelType: {
      type: String,
      default: "product",
    },
    modules: {
      type: Number,
      default: 66,
    },
    reprintMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: "onLabel",
      packQty: 50,
      qtyRows: [],
      labelList: [],
      templateList: [],
      templateLoading: false,
      selectedTemplateId: null,
      previewLoading: false,
      previewError: "",
      previewHasContent: false,
      specLoading: false,
      confirmLoading: false,
      specOptionsMap: {},
      _previewTimer: null,
      _syncRetry: 0,
    };
  },
  computed: {
    isMaterial() {
      return this.labelType === "material";
    },
    nounText() {
      return this.isMaterial ? this.$t("物料") : this.$t("产品");
    },
    labelName() {
      return this.isMaterial ? this.$t("物料标签") : this.$t("产品标签");
    },
    noLabel() {
      return this.isMaterial ? this.$t("物料编号") : this.$t("产品编号");
    },
    nameLabel() {
      return this.isMaterial ? this.$t("物料名称") : this.$t("产品名称");
    },
    dialogTitle() {
      return this.reprintMode
        ? this.$t("补打批次码")
        : this.$t("打印") + this.labelName;
    },
    selectedTemplate() {
      return (
        this.templateList.find(
          (item) => String(item.sysPrintId) === String(this.selectedTemplateId)
        ) || null
      );
    },
    selectedTemplateName() {
      return (this.selectedTemplate && this.selectedTemplate.sysPrintName) || this.$t("标签实时预览");
    },
    isPageLoading() {
      return this.specLoading || this.confirmLoading;
    },
    labelNumberOrderType() {
      return this.isMaterial ? "WLBQ" : "CPBQ";
    },
    canPreview() {
      return !!(
        this.allSpecReady &&
        this.selectedTemplate &&
        this.selectedTemplate.printTemplateDetails &&
        this.labelList.length
      );
    },
    needSpecSelect() {
      if (this.reprintMode) return false;
      return this.qtyRows.some((row) => this.specOptionsOf(row).length > 1);
    },
    multiSpecRows() {
      return this.qtyRows.filter((row) => this.specOptionsOf(row).length > 1);
    },
    allSpecReady() {
      return this.qtyRows.every((row) => {
        const list = this.specOptionsOf(row);
        return list.length <= 1 || row._specGroupId != null && row._specGroupId !== "";
      });
    },
    previewTip() {
      if (this.labelList.length > PREVIEW_MAX) {
        return (
          this.$t("共将生成") +
          " " +
          this.labelList.length +
          " " +
          this.$t("张标签") +
          "，" +
          this.$t("右侧仅预览前") +
          PREVIEW_MAX +
          this.$t("张")
        );
      }
      return "";
    },
    stepSpecClass() {
      return this.allSpecReady ? "done" : "on";
    },
    step1Class() {
      if (this.needSpecSelect && !this.allSpecReady) return "";
      return this.selectedTemplateId ? "done" : "on";
    },
    step2Class() {
      if (this.needSpecSelect && !this.allSpecReady) return "";
      return this.selectedTemplateId ? "on" : "";
    },
    modeText() {
      if (this.reprintMode) return this.$t("每批次一张");
      if (this.mode === "perPack") return this.$t("按每包拆签");
      if (this.mode === "perPiece") return this.$t("一件一页");
      return this.$t("一条一签");
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.bootstrap();
        } else {
          this.clearPreviewTimer();
          this.clearLabelPreview();
        }
      }
    },
    changeList: {
      handler() {
        if (this.visible) this.bootstrap();
      }
    }
  },
  beforeDestroy() {
    this.clearPreviewTimer();
    this.clearLabelPreview();
  },
  methods: {
    $t(value) {
      return value;
    },
    bootstrap() {
      try {
        this.initRows();
        this.loadSpecOptions();
        this.rebuildLabels();
        this.loadTemplates();
      } catch (e) {
        this.rebuildLabels();
        this.loadTemplates();
      }
    },
    formatSpec(row) {
      if (!row) return "";
      if (typeof row.specifications === "string" && row.specifications) {
        return row.specifications;
      }
      const list = parseSpecList(
        row.specifications ||
          row.materialSpecList ||
          row.materialCacheSpecList ||
          row.baseMaterialSpecDesc ||
          row.baseSpecgroupDesc
      );
      return specListShow(list);
    },
    formatUnit(row) {
      return (row && (row.baseMaterialUnitName || row.unitName)) || "";
    },
    specOptionsOf(row) {
      if (!row || row.materialId == null) return [];
      return this.specOptionsMap[row.materialId] || [];
    },
    specOptionLabel(spec) {
      if (!spec) return "";
      const name = spec.specGroupName || "";
      const text = spec.specificationsText || this.formatSpec(spec);
      if (name && text && name !== text) return name + " / " + text;
      return text || name || String(spec.specGroupId || "");
    },
    applySpecToRow(row, spec) {
      if (!row || !spec) return;
      row._specGroupId = spec.specGroupId;
      row.specGroupId = spec.specGroupId;
      row.specGroupName = spec.specGroupName;
      row.baseMaterialSpecDesc = spec.baseMaterialSpecDesc;
      row.materialCacheSpecList = spec.baseMaterialSpecDesc;
      row.materialSpecList = parseSpecList(
        spec.specifications || spec.baseMaterialSpecDesc
      );
      row.specifications = specListShow(row.materialSpecList);
    },
    onSpecChange(row) {
      const spec = this.specOptionsOf(row).find(
        (item) => String(item.specGroupId) === String(row._specGroupId)
      );
      if (spec) this.applySpecToRow(row, spec);
      this.rebuildLabels();
    },
    normalizeSpecGroup(item) {
      const spec = Object.assign({}, item);
      spec.materialSpecList = parseSpecList(
        spec.specifications || spec.baseMaterialSpecDesc
      );
      spec.specificationsText = specListShow(spec.materialSpecList);
      return spec;
    },
    loadSpecOptions() {
      // 协同端无产品详情权限，规格以单据明细为准，不再调 material/queryById
      this.specOptionsMap = {};
      (this.qtyRows || []).forEach((row) => {
        if (row.materialId == null || row.materialId === '') return;
        try {
          const spec = this.normalizeSpecGroup({
            specGroupId: row.specGroupId || row._specGroupId,
            specGroupName: row.specGroupName,
            baseMaterialSpecDesc: row.baseMaterialSpecDesc,
            specifications: row.specifications || row.baseMaterialSpecDesc
          });
          if (spec.specGroupId) {
            this.$set(this.specOptionsMap, row.materialId, [spec]);
            this.applySpecToRow(row, spec);
          }
        } catch (e) {
          /* ignore */
        }
      });
    },
    initRows() {
      this.mode = "onLabel";
      this.packQty = 50;
      this.qtyRows = (this.changeList || []).map((item) => {
        const copy = Object.assign({}, item);
        const exist = this.reprintMode
          ? Number(item.printQty || item._printQty || 1)
          : Number(item.printQty || item.baseAmount || item.baseQuantity);
        copy._printQty = Number.isFinite(exist) && exist > 0 ? Math.round(exist) : 1;
        copy._specGroupId = item.specGroupId || null;
        return copy;
      });
    },
    setMode(mode) {
      this.mode = mode;
      this.rebuildLabels();
    },
    normalizeQty(val) {
      let n = Number(val);
      if (!Number.isFinite(n) || n < 1) n = 1;
      return Math.round(n);
    },
    rebuildLabels() {
      const pack = this.normalizeQty(this.packQty);
      this.packQty = pack;
      const out = [];
      this.qtyRows.forEach((row) => {
        const qty = this.normalizeQty(row._printQty);
        row._printQty = qty;
        if (this.mode === "onLabel") {
          out.push(this.makeLabel(row, qty, "1/1"));
        } else if (this.mode === "perPiece") {
          for (let i = 0; i < qty; i++) {
            out.push(this.makeLabel(row, 1, i + 1 + "/" + qty));
          }
        } else {
          const full = Math.floor(qty / pack);
          const tail = qty % pack;
          const total = full + (tail ? 1 : 0) || 1;
          let n = 0;
          for (let i = 0; i < full; i++) {
            n++;
            out.push(this.makeLabel(row, pack, n + "/" + total));
          }
          if (tail > 0) {
            n++;
            out.push(this.makeLabel(row, tail, n + "/" + total));
          }
          if (!full && !tail) {
            out.push(this.makeLabel(row, 1, "1/1"));
          }
        }
      });
      const total = out.length;
      this.labelList = out.map((item, idx) => {
        item.printIndex = idx + 1;
        item.printTotal = total;
        item.printid = idx + 1;
        item.printId = idx + 1;
        item._page = idx + 1 + "/" + total;
        return item;
      });
      this.scheduleTemplatePreview();
    },
    makeLabel(row, qty, seq) {
      const item = Object.assign({}, row);
      item.printQty = qty;
      item.printSeq = seq;
      item.productQtySeq = seq;
      item.baseAmount = qty;
      item.baseQuantity = qty;
      item.specifications = this.formatSpec(row);
      item.qrcode = this.buildLabelQrcode(row, qty);
      return item;
    },
    buildLabelQrcode(row, qty) {
      if (this.reprintMode) {
        return JSON.stringify({
          modules: LABEL_QRCODE_MODULES,
          productId: row && row.materialId ? String(row.materialId) : "",
          batchNumber: row && row.batchNumber
        });
      }
      let obj = {};
      const raw = row && row.qrcode;
      if (raw) {
        try {
          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (parsed && typeof parsed === "object") {
            obj = Object.assign({}, parsed);
          }
        } catch (e) {
          obj = { content: String(raw) };
        }
      }
      obj.modules = LABEL_QRCODE_MODULES;
      if (!obj.productId && row && row.materialId) {
        obj.productId = String(row.materialId);
      }
      if (row && row.specGroupId) {
        obj.specGroupId = row.specGroupId;
      }
      obj.qty = qty;
      obj.printQty = qty;
      if (row && row.labelCode) {
        obj.labelCode = row.labelCode;
      }
      return JSON.stringify(obj);
    },
    handleClose() {
      this.clearPreviewTimer();
      this.clearLabelPreview();
      this.confirmLoading = false;
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    async assignLabelCodes(list) {
      const count = (list || []).length;
      if (!count) return list;
      const numberList = await this.fetchLabelCodes(count);
      if (!numberList.length) {
        throw new Error("empty");
      }
      list.forEach((item, idx) => {
        const code = String(numberList[idx] || "").trim();
        if (!code) return;
        item.labelCode = code;
        item.qrcode = this.buildLabelQrcode(item, item.printQty);
      });
      return list;
    },
    pickNumberValue(res) {
      const data = res && res.data && res.data.data;
      if (Array.isArray(data)) return data;
      if (data == null || data === "") return [];
      return [data];
    },
    async fetchLabelCodes(count) {
      const list = [];
      for (let i = 0; i < count; i++) {
        const res = await sysNumber_queryNumberLength({
          length: 8,
          orderType: this.labelNumberOrderType
        });
        const code = this.pickNumberValue(res)[0];
        if (code == null || String(code).trim() === "") {
          throw new Error("empty");
        }
        list.push(String(code).trim());
      }
      return list;
    },
    loadTemplates() {
      this.templateLoading = true;
      this.templateList = [];
      sysPrint_list({
        sysPrintDtoFilter: {
          modules: this.modules,
          printMode: "normal",
        },
        pageParam: { total: 0, pageNum: 1, pageSize: 200 },
      })
        .then((res) => {
          const list = ((res.data && res.data.data && res.data.data.dataList) || []).filter(
            (item) =>
              Number(item.modules) === Number(this.modules) &&
              !isOfficeFileTemplateType(item.sysPrintType, this.$t("模板")) &&
              this.matchProductLabelType(item)
          );
          this.templateList = list;
          const preferred = resolvePreferredLabelTemplate(list, this.modules);
          this.selectedTemplateId = preferred
            ? preferred.sysPrintId
            : list.length
            ? list[0].sysPrintId
            : null;
        })
        .catch(() => {
          this.templateList = [];
          this.selectedTemplateId = null;
        })
        .finally(() => {
          this.templateLoading = false;
          this.scheduleTemplatePreview();
        });
    },
    matchProductLabelType(item) {
      const typeName = "产品标签";
      const wanted = this.$t(typeName);
      const t = String((item && item.sysPrintType) || "").trim();
      return t === typeName || t === wanted;
    },
    onTemplateChange() {
      if (this.selectedTemplateId == null) return;
      try {
        localStorage.setItem(
          lastTemplateStorageKey(this.modules, "normal"),
          JSON.stringify(this.selectedTemplateId)
        );
      } catch (e) {
        /* ignore */
      }
      this.scheduleTemplatePreview();
    },
    clearPreviewTimer() {
      if (this._previewTimer) {
        clearTimeout(this._previewTimer);
        this._previewTimer = null;
      }
    },
    clearLabelPreview() {
      this.previewHasContent = false;
      this.previewLoading = false;
      this.previewError = "";
      try {
        const el = this.$refs.labelTplPreview;
        if (el) el.innerHTML = "";
      } catch (e) {
        /* ignore */
      }
    },
    getPlainTemplatePanel() {
      const raw = this.selectedTemplate && this.selectedTemplate.printTemplateDetails;
      if (raw == null || raw === "") return null;
      if (typeof raw === "string") return JSON.parse(raw);
      return JSON.parse(JSON.stringify(raw));
    },
    createHiprintTemplate() {
      const hp = hiprint || window.hiprint;
      if (!hp || !hp.PrintTemplate) {
        throw new Error("hiprint unavailable");
      }
      const panel = this.getPlainTemplatePanel();
      if (!panel || typeof panel !== "object") {
        throw new Error("invalid template panel");
      }
      const tpl = createNormalizedHiprintTemplate(panel);
      if (!tpl || typeof tpl.getHtml !== "function" || !tpl.printPanels || !tpl.printPanels.length) {
        throw new Error("empty print panels");
      }
      return tpl;
    },
    buildPreviewPrintList(limit) {
      const max = limit == null ? PREVIEW_MAX : limit;
      return this.labelList.slice(0, max).map((item, idx) => {
        const row = Object.assign({}, item);
        row.printid = idx + 1;
        row.printId = idx + 1;
        row.imageBaseUrl = projectConfig.imageBaseUrl;
        if (row.baseImgUrl) {
          row.baseMaterialImg = projectConfig.imageBaseUrl + row.baseImgUrl;
        }
        return row;
      });
    },
    scheduleTemplatePreview() {
      this.clearPreviewTimer();
      if (!this.visible) return;
      this.previewLoading = true;
      this.previewError = "";
      this._previewTimer = setTimeout(() => {
        this._previewTimer = null;
        this.refreshTemplatePreview();
      }, 280);
    },
    refreshTemplatePreview() {
      if (!this.canPreview) {
        this.clearLabelPreview();
        if (!this.selectedTemplate) {
          this.previewError = this.$t("未选择打印模板");
        }
        return;
      }
      this.$nextTick(() => {
        try {
          if (typeof window !== "undefined") {
            window.$ = $;
            window.jQuery = $;
          }
          const tpl = this.createHiprintTemplate();
          const printData = this.buildPreviewPrintList();
          const ref = this.$refs.labelPrintPreview;
          if (ref && typeof ref.showSilent === "function") {
            this._syncRetry = 0;
            this.previewLoading = true;
            ref.showSilent(
              tpl,
              printData,
              undefined,
              this.modules,
              [],
              0,
              this.selectedTemplateName
            );
            return;
          }
          this.renderPreviewDirect(tpl, printData);
        } catch (e) {
          this.previewLoading = false;
          this.previewHasContent = false;
          this.previewError =
            this.$t("预览渲染失败") + (e && e.message ? "：" + e.message : "");
        }
      });
    },
    renderPreviewDirect(tpl, printData) {
      const dest = this.$refs.labelTplPreview;
      if (!dest) {
        this.previewLoading = false;
        return;
      }
      dest.innerHTML = "";
      printData.forEach((item) => {
        const div = tpl.getHtml(item);
        dest.appendChild(div[0]);
      });
      this.stripPreviewPaperFrame(dest);
      this.previewHasContent = true;
      this.previewError = "";
      this.previewLoading = false;
    },
    stripPreviewPaperFrame(root) {
      if (!root) return;
      const wrappers = root.querySelectorAll(".hiprint-printTemplate, .hiprint-printPanel");
      wrappers.forEach((el) => {
        el.style.border = "none";
        el.style.boxShadow = "none";
        el.style.outline = "none";
      });
      const papers = root.querySelectorAll(".hiprint-printPaper");
      papers.forEach((el) => {
        el.style.border = "1px solid #c0c4cc";
        el.style.boxShadow = "none";
        el.style.outline = "none";
        el.style.background = "#fff";
      });
    },
    isPreviewPaperReady(src) {
      if (!src) return false;
      if (src.querySelector(".hiprint-printPaper, .hiprint-printTemplate")) return true;
      const text = (src.textContent || "").replace(/\s+/g, "");
      if (!text || text === this.$t("加载中").replace(/\s+/g, "")) return false;
      return src.querySelector("[class*='hiprint']") != null;
    },
    syncPreviewFromSilent() {
      const ref = this.$refs.labelPrintPreview;
      const dest = this.$refs.labelTplPreview;
      if (!ref || !dest) {
        this.scheduleSyncRetry();
        return;
      }
      const silentId =
        (typeof ref.getSilentPreviewDomId === "function" && ref.getSilentPreviewDomId()) ||
        null;
      const src = silentId ? document.getElementById(silentId) : null;
      if (this.isPreviewPaperReady(src)) {
        dest.innerHTML = src.innerHTML;
        this.stripPreviewPaperFrame(dest);
        this.previewHasContent = true;
        this.previewError = "";
        this.previewLoading = false;
        this._syncRetry = 0;
        return;
      }
      this.scheduleSyncRetry();
    },
    scheduleSyncRetry() {
      if (!this._syncRetry) this._syncRetry = 0;
      if (this._syncRetry < 20) {
        this._syncRetry += 1;
        setTimeout(() => this.syncPreviewFromSilent(), 120);
      } else {
        this._syncRetry = 0;
        this.previewLoading = false;
        if (!this.previewHasContent) {
          this.previewError = this.$t("预览渲染失败");
        }
      }
    },
    buildFinalPrintList(list) {
      return (list || []).map((item, idx) => {
        const row = Object.assign({}, item);
        row.printid = idx + 1;
        row.printId = idx + 1;
        row.imageBaseUrl = projectConfig.imageBaseUrl;
        if (row.baseImgUrl) {
          row.baseMaterialImg = projectConfig.imageBaseUrl + row.baseImgUrl;
        }
        return row;
      });
    },
    showLabelPrintPreview(list) {
      if (!this.canPreview) {
        this.$message.warning(this.$t("未选择打印模板"));
        return;
      }
      try {
        const tpl = this.createHiprintTemplate();
        const printData = this.buildFinalPrintList(list);
        if (!printData.length) {
          this.$message.warning(this.$t("请填写数量后重新生成"));
          return;
        }
        const ref = this.$refs.labelPrintPreview;
        if (!ref || typeof ref.show !== "function") {
          this.$message.warning(this.$t("暂无预览"));
          return;
        }
        ref.show(tpl, printData);
      } catch (e) {
        this.$message.error(
          this.$t("预览渲染失败") + (e && e.message ? "：" + e.message : "")
        );
      }
    },
    openLargePreview() {
      if (this.isPageLoading) return;
      this.showLabelPrintPreview(this.buildPreviewPrintList(this.labelList.length));
    },
    handleConfirm() {
      if (this.isPageLoading) return;
      if (!this.allSpecReady) {
        this.$message.warning(this.$t("请选择规格"));
        return;
      }
      if (!this.selectedTemplate || !this.selectedTemplate.printTemplateDetails) {
        this.$message.warning(this.$t("未选择打印模板"));
        return;
      }
      this.rebuildLabels();
      if (!this.labelList.length) {
        this.$message.warning(this.$t("请填写数量后重新生成"));
        return;
      }
      if (this.labelList.length > 200) {
        this.$confirm(
          this.$t("将生成较多标签，是否继续") + "（" + this.labelList.length + "）？",
          this.$t("提示"),
          { type: "warning" }
        )
          .then(() => {
            this.emitConfirm();
          })
          .catch(() => {});
        return;
      }
      this.emitConfirm();
    },
    showLabelCodeError(err) {
      if (err && err.message === "short") {
        this.$message.error(this.$t("编码批量生成失败：返回条数不足"));
        return;
      }
      if (err && err.message === "empty") {
        this.$message.error(this.$t("编码批量生成失败：存在空流水号"));
        return;
      }
      this.$message.error(this.$t("编码批量生成失败"));
    },
    async emitConfirm() {
      if (this.confirmLoading) return;
      this.confirmLoading = true;
      try {
        if (!this.reprintMode) {
          try {
            await this.assignLabelCodes(this.labelList);
          } catch (e) {
            this.showLabelCodeError(e);
            this.$message.warning("将继续打印，标签码可能为空");
          }
        }
        const list = this.labelList.map((item) => {
          const row = Object.assign({}, item);
          delete row._printQty;
          delete row._page;
          return row;
        });
        this.showLabelPrintPreview(list);
        this.$nextTick(() => {
          this.$emit("confirm", {
            list,
            templateDetails: this.selectedTemplate.printTemplateDetails,
            template: this.selectedTemplate,
          });
        });
      } catch (e) {
        this.showLabelCodeError(e);
      } finally {
        this.confirmLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.plq-body {
  position: relative;
  min-height: 420px;
}
.plq-body.is-busy .plq-body__inner {
  pointer-events: none;
  user-select: none;
}
.plq-skeleton {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 8;
  background: #fff;
  padding: 0 0 4px;
}
.plq-skeleton__steps,
.plq-skeleton__modes,
.plq-skeleton__main {
  display: flex;
  gap: 8px;
}
.plq-skeleton__steps {
  margin-bottom: 8px;
}
.plq-skeleton__step,
.plq-skeleton__bar,
.plq-skeleton__line,
.plq-skeleton__mode,
.plq-skeleton__paper {
  background: linear-gradient(90deg, #f2f3f5 25%, #e9e9eb 37%, #f2f3f5 63%);
  background-size: 400% 100%;
  animation: plq-shimmer 1.2s ease-in-out infinite;
  border-radius: 4px;
}
.plq-skeleton__step {
  flex: 1;
  height: 28px;
}
.plq-skeleton__bar {
  height: 36px;
  margin-bottom: 8px;
}
.plq-skeleton__main {
  align-items: stretch;
  min-height: 320px;
}
.plq-skeleton__card {
  flex: 1;
  min-width: 0;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.plq-skeleton__preview {
  flex: 0 0 400px;
}
.plq-skeleton__modes {
  margin: 10px 0 8px;
}
.plq-skeleton__mode {
  flex: 1;
  height: 42px;
}
.plq-skeleton__table {
  margin-top: 8px;
}
.plq-skeleton__line {
  height: 14px;
  margin-bottom: 10px;
}
.plq-skeleton__line.w-40 {
  width: 40%;
}
.plq-skeleton__line.w-50 {
  width: 50%;
}
.plq-skeleton__line.w-70 {
  width: 70%;
}
.plq-skeleton__paper {
  height: 120px;
  margin-top: 12px;
}
@keyframes plq-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}
.plq-steps {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}
.plq-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  font-size: 12px;
  color: #909399;
}
.plq-step.on {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409eff;
  font-weight: 600;
}
.plq-step.done {
  background: #f0f9eb;
  border-color: #c2e7b0;
  color: #67c23a;
}
.plq-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #dcdfe6;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}
.plq-step.on .plq-dot {
  background: #409eff;
}
.plq-step.done .plq-dot {
  background: #67c23a;
}
.plq-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 6px 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  min-height: 36px;
}
.plq-bar__title {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}
.plq-bar__list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  flex: 1;
  min-width: 0;
}
.plq-bar__item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.plq-bar__name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #606266;
}
.plq-bar__select {
  width: 260px;
}
.plq-bar ::v-deep .el-input__inner {
  height: 26px;
  line-height: 26px;
}
.plq-tpl__label {
  font-size: 13px;
  color: #606266;
}
.plq-tpl__opt {
  display: flex;
  width: 100%;
}
.plq-tpl__type {
  margin-left: auto;
  font-size: 12px;
  color: cadetblue;
}
.plq-tpl__empty {
  font-size: 12px;
  color: #909399;
}
.plq-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 8px;
  min-height: 0;
}
.plq-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.plq-card__hd {
  position: relative;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;
}
.plq-card__bd {
  padding: 8px 10px;
}
.plq-table ::v-deep .el-table__header th {
  padding: 4px 0;
  height: 28px;
  line-height: 20px;
}
.plq-table ::v-deep .el-table__body td {
  padding: 2px 0;
  height: 32px;
}
.plq-table ::v-deep .el-table__row {
  height: 32px;
}
.plq-table ::v-deep .cell {
  padding-left: 8px;
  padding-right: 8px;
  line-height: 22px;
}
.plq-modes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.plq-mode {
  flex: 1;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}
.plq-mode:hover {
  border-color: #409eff;
}
.plq-mode.active {
  border-color: #409eff;
  background: #ecf5ff;
  color: #409eff;
}
.plq-mode b {
  display: block;
  font-size: 13px;
  margin-bottom: 1px;
  line-height: 1.3;
}
.plq-mode span {
  display: block;
  color: #909399;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.plq-mode.active span {
  color: #79bbff;
}
.plq-pack {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #606266;
}
.plq-summary {
  font-size: 12px;
  color: #606266;
  margin-top: 6px;
}
.plq-summary strong {
  color: #409eff;
  font-size: 14px;
  margin: 0 4px;
}
.plq-note {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  margin-top: 4px;
}
.plq-preview {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.plq-preview .plq-card__hd {
  padding-right: 72px;
}
.plq-preview__zoom {
  position: absolute;
  right: 8px;
  top: 4px;
}
.plq-preview__bd {
  flex: 1;
  background: #f5f7fa;
  overflow: auto;
  max-height: 380px;
  position: relative;
  padding: 8px 6px 4px;
}
.plq-preview__error {
  font-size: 12px;
  color: #f56c6c;
  margin-bottom: 6px;
}
.plq-preview__tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}
.plq-preview__loading {
  font-size: 12px;
  color: #909399;
  padding: 8px 0;
}
.plq-tpl-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.plq-tpl-preview ::v-deep .hiprint-printTemplate {
  zoom: 0.55;
  border: none !important;
  box-shadow: none !important;
}
.plq-tpl-preview ::v-deep .hiprint-printPanel {
  margin: 0 auto 10px !important;
  border: none !important;
  box-shadow: none !important;
}
.plq-tpl-preview ::v-deep .hiprint-printPaper {
  margin: 0 auto 8px !important;
  border: 1px solid #909399 !important;
  box-shadow: none !important;
  background: #fff;
}
.plq-empty {
  text-align: center;
  color: #909399;
  padding: 24px 0;
  font-size: 13px;
}
.btn-teal {
  background: #13c2c2;
  border-color: #13c2c2;
}
.btn-teal:hover,
.btn-teal:focus {
  background: #36cfc9;
  border-color: #36cfc9;
}
</style>

<style>
.print-label-qty-dialog .el-dialog__header {
  padding: 10px 16px 8px;
}
.print-label-qty-dialog .el-dialog__body {
  padding: 8px 16px 6px;
}
.print-label-qty-dialog .el-dialog__footer {
  padding: 8px 16px 12px;
}
</style>
